import React, { useState } from 'react';
import { useImageUpload } from '../../utils/useImageUpload';

interface ProfileScreenProps {
  user: any;
  loading: boolean;
  onUpdateProfile: (data: { name?: string; password?: string; profileImage?: string | null }) => Promise<boolean>;
  onSignOut: () => void;
}

export const ProfileScreen: React.FC<ProfileScreenProps> = ({ user, loading, onUpdateProfile, onSignOut }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(user?.profileImage || null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { uploading, showPicker } = useImageUpload({
    purpose: 'profile',
    onSuccess: (url) => { setProfileImage(url); },
    onError: (msg) => { setValidationError(msg); },
  });

  const getAssetUrl = (path?: string) => (!path ? '' : path.startsWith('http') ? path : path);

  const handleStartEdit = () => {
    setName(user?.name || '');
    setPassword('');
    setConfirmPassword('');
    setProfileImage(user?.profileImage || null);
    setValidationError(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setValidationError(null);
    if (!name.trim()) { setValidationError('Name cannot be empty'); return; }
    if (password && password.length < 6) { setValidationError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setValidationError('Passwords do not match'); return; }
    const updateData: { name?: string; password?: string; profileImage?: string | null } = { name, profileImage };
    if (password) updateData.password = password;
    const success = await onUpdateProfile(updateData);
    if (success) setIsEditing(false);
  };

  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopyReferral = async () => {
    const code = user?.referral_code || '';
    if (!code) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(code);
      }
    } catch (e) {}
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const avatarInitial = (name || user?.name || 'P').charAt(0).toUpperCase();
  const displayAvatar = profileImage ? getAssetUrl(profileImage) : null;

  return (
    <div className="pwa-tab-scroll">

      {/* Avatar header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '20px' }}>
        <div
          onClick={isEditing ? showPicker : undefined}
          style={{ width: '76px', height: '76px', borderRadius: '50%', background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: '800', color: '#fff', marginBottom: '12px', cursor: isEditing ? 'pointer' : 'default', position: 'relative', overflow: 'hidden' }}
        >
          {displayAvatar ? <img src={displayAvatar} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : avatarInitial}
          {isEditing && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📷</div>
          )}
        </div>

        <h2 style={{ fontSize: 'clamp(18px, 5vw, 20px)', fontWeight: '900', color: '#fff' }}>{user?.name}</h2>
        <span className="pwa-status-pill pwa-status-purple" style={{ marginTop: '6px' }}>PLAYER ACCOUNT</span>

        {isEditing && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '14px', width: '100%', justifyContent: 'center' }}>
            <button onClick={showPicker} disabled={uploading} className="pwa-primary-btn" style={{ marginTop: 0, padding: '8px 16px', fontSize: '11px', width: 'auto' }}>
              {uploading ? 'UPLOADING...' : 'CHANGE PHOTO'}
            </button>
            {profileImage && (
              <button onClick={() => setProfileImage(null)} className="pwa-secondary-btn" style={{ marginTop: 0, padding: '8px 16px', fontSize: '11px', width: 'auto', borderColor: 'rgba(239,68,68,0.35)', color: '#ef4444' }}>
                REMOVE
              </button>
            )}
          </div>
        )}
      </div>

      {validationError && (
        <div style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '13px', fontWeight: '700', textAlign: 'center' }}>
          {validationError}
        </div>
      )}

      {/* Referral Code Card */}
      <div className="pwa-info-card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>YOUR REFERRAL CODE</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '900', color: '#F5B942', fontFamily: 'monospace' }}>
              {user?.referral_code || 'REF-8X9P2K'}
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
              Share with friends to invite them
            </div>
          </div>
          <button
            onClick={handleCopyReferral}
            className="pwa-primary-btn"
            style={{ marginTop: 0, width: 'auto', padding: '8px 14px', fontSize: '12px', background: copiedCode ? '#10b981' : '#7C3AED' }}
          >
            {copiedCode ? 'COPIED! ✓' : 'COPY CODE'}
          </button>
        </div>
      </div>

      {/* Info rows */}
      <div className="pwa-info-card">
        <div className="pwa-info-row">
          <div className="pwa-info-row-label">FULL NAME</div>
          {isEditing
            ? <input type="text" className="pwa-auth-input" value={name} onChange={(e) => setName(e.target.value)} style={{ marginTop: '6px' }} />
            : <div className="pwa-info-row-value">{user?.name}</div>
          }
        </div>

        <div className="pwa-info-row">
          <div className="pwa-info-row-label">EMAIL ADDRESS (READ-ONLY)</div>
          <div className="pwa-info-row-value" style={{ color: 'rgba(255,255,255,0.4)' }}>{user?.email}</div>
        </div>

        <div className="pwa-info-row">
          <div className="pwa-info-row-label">PHONE NUMBER (READ-ONLY)</div>
          <div className="pwa-info-row-value" style={{ color: 'rgba(255,255,255,0.4)' }}>{user?.phone}</div>
        </div>

        <div className="pwa-info-row">
          <div className="pwa-info-row-label">PHONE VERIFIED</div>
          <div className="pwa-info-row-value">
            <span className={`pwa-status-pill ${user?.phone_verified ? 'pwa-status-green' : 'pwa-status-amber'}`}>
              {user?.phone_verified ? '✓  VERIFIED' : '⚠  UNVERIFIED'}
            </span>
          </div>
        </div>

        {isEditing && (
          <>
            <div className="pwa-info-row">
              <div className="pwa-info-row-label">NEW PASSWORD (OPTIONAL)</div>
              <input type="password" className="pwa-auth-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave blank to keep current" style={{ marginTop: '6px' }} />
            </div>
            <div className="pwa-info-row">
              <div className="pwa-info-row-label">CONFIRM NEW PASSWORD</div>
              <input type="password" className="pwa-auth-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" style={{ marginTop: '6px' }} />
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      {isEditing ? (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <button onClick={handleSave} disabled={loading || uploading} className="pwa-primary-btn" style={{ flex: 1, marginTop: 0 }}>
            {loading ? 'SAVING...' : 'SAVE CHANGES'}
          </button>
          <button onClick={() => setIsEditing(false)} className="pwa-secondary-btn" style={{ flex: 1, marginTop: 0 }}>
            CANCEL
          </button>
        </div>
      ) : (
        <button onClick={handleStartEdit} className="pwa-primary-btn" style={{ marginTop: 0, marginBottom: '12px' }}>
          EDIT PROFILE
        </button>
      )}

      {!isEditing && (
        <button
          onClick={onSignOut}
          style={{ width: '100%', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '14px', padding: '14px', color: '#ef4444', fontSize: '14px', fontWeight: '800', cursor: 'pointer', marginBottom: '24px' }}
        >
          LOG OUT
        </button>
      )}
    </div>
  );
};
